import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { InboundEmail } from "@/services/inbound-email.service";
import { formatEmailDate } from "./status";

/** The original email, collapsed by default to keep the draft in view. */
export function EmailPreview({ email, defaultOpen = false }: { email: InboundEmail; defaultOpen?: boolean }) {
	const [open, setOpen] = useState(defaultOpen);
	const list = (kind: "to" | "cc") =>
		email.recipients
			.filter((r) => r.kind === kind)
			.map((r) => r.name || r.email)
			.join(", ");

	return (
		<section className="rounded-xl border border-border bg-surface">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
			>
				<span className="min-w-0">
					<span className="block text-sm font-medium text-foreground">Original email</span>
					<span className="block truncate text-xs text-muted">
						{email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}
						{email.sent_at && ` · ${formatEmailDate(email.sent_at)}`}
					</span>
				</span>
				<ChevronDown className={cn("h-4 w-4 shrink-0 text-muted transition-transform", open && "rotate-180")} aria-hidden />
			</button>
			{open && (
				<div className="border-t border-border px-4 py-3">
					<dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
						<dt className="text-muted">To</dt>
						<dd className="text-foreground">{list("to") || "-"}</dd>
						{list("cc") && (
							<>
								<dt className="text-muted">Cc</dt>
								<dd className="text-foreground">{list("cc")}</dd>
							</>
						)}
					</dl>
					<pre className="max-h-[480px] overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm text-foreground">
						{email.body_text || "(empty)"}
					</pre>
					{email.body_truncated && (
						<p className="mt-2 text-xs text-warning">The email was longer than TaskHub stores; the rest was cut off.</p>
					)}
				</div>
			)}
		</section>
	);
}
